/**
 * ChatPanel (WS-H) — the transcript column's root. Since the 2026-08-28
 * redesign every session is a plain chat: the setup interview, the guide
 * empty state and the learner-chip machinery are gone; this module renders
 * ONE thing — the session view for the sid on the route (the sessionId prop
 * path from Phase 1 is the only path).
 *
 * Session header (round 3): the back arrow, the title, and Export. Nothing
 * else. The commit sha and the last-refresh time left with the owner's note
 * ("I don't know if commit sha and last refreshed at needs to be at the top
 * of every session") — that is provenance, it belongs to the codebase, and
 * the Codebase page already carries it in full. The model/effort pair left
 * too, downward: it lives on the composer's bottom edge now, where every
 * other chat app puts it, and showing it twice was never the plan.
 *
 * Round 5 added the two SESSION-wide readouts (SessionMeter): how full the
 * context is, and what the conversation has cost so far. Round 7 moved them
 * down onto the composer, where Conductor keeps them (owner: "The context and
 * limits should come at the input text field like Conductor"), so the header
 * is back to three things and the numbers sit where the spending decision is
 * made. Both are quiet, both vanish when the numbers behind them are unknown,
 * and neither is repeated under a turn.
 *
 * The composer runs the FULL width of the column here (round 7, owner: "The
 * input text field should span the whole chat width"). The centred 768px
 * measure is the product home's; in a ~780px column it only ever showed as
 * dead margin down both sides.
 *
 * Export… is one internal agent turn → markdown preview.
 *
 * Unmounting NEVER aborts an in-flight stream: collapse keeps the reply
 * streaming full-canvas, and route-away keeps it streaming behind the
 * sidebar's session status glyphs. A stream ends only at its Stop button or
 * logout (streamController).
 */

import { useQueryClient } from '@tanstack/react-query';
import { lazy, Suspense, useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { queryKeys, useArtifact, useMe, useProduct } from '../../api/queries';
import type { ChatSessionDetail, Effort, PatchChatSessionRequest, Ulid } from '../../api/types';
import { useGraph } from '../../lib/artifacts';
import { productPath } from '../../lib/deepLink';
import { useWorkspaceIds } from '../../lib/routeScope';
import { Button, SkeletonLine } from '../../lib/ui';
import { CostPreviewDialog } from '../refresh/CostPreviewDialog';
import { useChatStore, useSessionDiagrams, useSessionLive } from '../../stores/chatStore';
import { useRuntimeMap } from '../liveinfra/api';
import { useViewEnv } from '../liveinfra/envStore';
import { clampViewEnv } from '../liveinfra/types';
import { focusFor, orderedSelection } from '../diagram/selection';
import { patchChatSession } from './chatApi';
import { ChatComposer } from './NewComposer';
import { ComposerPickers } from './ComposerPickers';
import { MessageList } from './MessageList';
import {
  EMPTY_SOURCES,
  mentionCandidates,
  mentionToWire,
  type MentionCandidate,
  type MentionSources,
} from './mentions';
import { SessionMeter } from './SessionMeter';
import { FALLBACK_EFFORT, FALLBACK_MODEL } from './labels';
import { useChatSession } from './useChatSession';

/** Said under the composer when the controller refuses a send (2026-09-08). */
const SEND_REFUSED = 'A reply is already in progress. Wait for it to finish, or stop it, then send again.';

/** The export dialog and its markdown preview load on the first Export… (2026-09-08). */
const ExportDialog = lazy(() => import('./ExportDialog').then((m) => ({ default: m.ExportDialog })));

/** The flows artifact, as much of it as the picker reads. */
interface FlowsArtifactSlice {
  flows?: Record<string, { title?: string; plain_summary?: string }>;
}

/**
 * What the reader can name with `@` (DECISIONS #95/#96), from BOTH views
 * whatever tab is up: the board's parts, the infrastructure's services,
 * queues and stores (the runtime map the pane already fetched), and the
 * repo's flows and map once the picker is first opened (`wanted`), never
 * before, because the map of a real codebase is megabytes and a session
 * that never types `@` should never fetch it.
 */
function useMentionSources(
  repoId: Ulid | undefined,
  wanted: boolean,
  parts: MentionSources['parts'],
  runtime: MentionSources['entities'],
): MentionSources {
  const rid = wanted ? repoId : undefined;
  const flows = useArtifact<FlowsArtifactSlice>(rid, 'flows');
  const graph = useGraph(rid);
  return useMemo(() => {
    const flowRows = Object.entries(flows.data?.flows ?? {}).map(([id, flow]) => ({
      id,
      title: flow.title ?? id,
      hint: flow.plain_summary ?? null,
    }));
    const index = graph.data;
    const areas = index === undefined ? [] : index.dirIds.map((id) => ({ id, hint: null }));
    const fromGraph =
      index === undefined
        ? []
        : [...index.entities.values()].map((e) => ({ id: e.id, label: e.label, kind: e.kind }));
    const seen = new Set(runtime.map((e) => e.id));
    const entities = [...runtime, ...fromGraph.filter((e) => !seen.has(e.id))];
    const files =
      index === undefined
        ? []
        : Object.values(index.artifact.nodes).flatMap((node) => node.files.map((f) => f.path));
    return { parts, flows: flowRows, areas, entities, files };
  }, [parts, runtime, flows.data, graph.data]);
}

/**
 * The session's own model/effort overrides, written straight to the row.
 * The pickers themselves are the composer's (ComposerPickers); this is the
 * wiring: PATCH per change, null clears the override back to inherited, and
 * everything disables while a turn streams because the server answers 409
 * generation_active to a session write mid-generation.
 */
function SessionPickers({
  sessionId,
  productId,
  sessionModel,
  sessionEffort,
  streaming,
  onError,
}: {
  sessionId: Ulid;
  productId: Ulid;
  sessionModel: string | null;
  sessionEffort: Effort | null;
  streaming: boolean;
  onError: (message: string | null) => void;
}) {
  const qc = useQueryClient();
  const product = useProduct(productId);
  const me = useMe();

  const defaultModel = product.data?.default_model ?? me.data?.default_model ?? FALLBACK_MODEL;
  const defaultEffort: Effort =
    product.data?.default_effort ?? me.data?.default_effort ?? FALLBACK_EFFORT;

  const patch = (body: PatchChatSessionRequest) => {
    onError(null);
    // The meter moves on the click (2026-09-10, owner: "changing effort level
    // is slow"): the cached session takes the change now, the server's row
    // replaces it, and an error puts the old one back.
    const key = queryKeys.session(sessionId);
    const before = qc.getQueryData<ChatSessionDetail>(key);
    if (before !== undefined) qc.setQueryData(key, { ...before, ...body });
    void patchChatSession(sessionId, body)
      .then((summary) => {
        const current = qc.getQueryData<ChatSessionDetail>(key);
        if (current !== undefined) qc.setQueryData(key, { ...current, ...summary });
      })
      .catch((err: unknown) => {
        if (before !== undefined) qc.setQueryData(key, before);
        onError(err instanceof Error ? err.message : 'Could not change the session.');
      });
  };

  return (
    <ComposerPickers
      model={sessionModel}
      effort={sessionEffort}
      defaultModel={defaultModel}
      defaultEffort={defaultEffort}
      disabled={streaming}
      disabledReason="Changes wait until the current reply finishes."
      modelTestId="session-model-picker"
      effortTestId="session-effort-meter"
      onModel={(model) => patch({ model })}
      onEffort={(effort) => patch({ effort })}
    />
  );
}

export function SessionView({
  projectId,
  productId,
  sessionId,
  onHome,
}: {
  projectId: Ulid;
  productId: Ulid;
  sessionId: Ulid;
  /** where "←" leads; defaults to the product home */
  onHome?: () => void;
}) {
  const navigate = useNavigate();
  const { session, send, stop } = useChatSession(sessionId);
  const live = useSessionLive(sessionId);
  const streaming = live.streaming;
  const composerDraft = useChatStore((s) => s.composerDraft);
  const setComposerDraft = useChatStore((s) => s.setComposerDraft);
  const [exporting, setExporting] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  // Read but not written (the wizard's free extract landed a graph and no
  // narration): the same line and button the product home carries, so a
  // reader in the chat learns why answers take the long road.
  const [writeMapOpen, setWriteMapOpen] = useState(false);
  const productForMap = useProduct(productId);
  const unwrittenRepo =
    productForMap.data?.repos.find(
      (r) => r.node_count !== null && r.flow_count === null && r.active_job === null,
    ) ?? null;

  // What the question is ABOUT (DECISIONS #95/#96): the `@Label` tokens in
  // the composer's text. A part of the board among them is the picked set
  // (the pane marks it); everything else rides the send as mentions. A
  // click on the drawing arrives here as a request to put a token in.
  const setDiagramSelection = useChatStore((s) => s.setDiagramSelection);
  const composerRequest = useChatStore((s) => s.composerRequest);
  const requestComposer = useChatStore((s) => s.requestComposer);
  const diagrams = useSessionDiagrams(sessionId);
  const board = useMemo(() => diagrams.at(-1) ?? null, [diagrams]);
  const [mentionsWanted, setMentionsWanted] = useState(false);
  const wantedRuntimeEnv = useViewEnv(projectId.length > 0 ? projectId : '');
  // The runtime entities the picker lists: asked for once the picker is
  // first opened, like the flows and the map below, and never polled from
  // here (the pane's own infrastructure view is the one that keeps looking).
  const runtimeMap = useRuntimeMap(
    mentionsWanted && projectId.length > 0 ? projectId : undefined,
    undefined,
    { poll: false },
  );
  const runtimeEnv = clampViewEnv(wantedRuntimeEnv, Object.keys(runtimeMap.data?.environments ?? {}));
  const runtimeEntities = useMemo(() => {
    const doc = runtimeMap.data;
    if (doc === undefined) return [];
    const envDoc = doc.environments[runtimeEnv] ?? doc.environments[doc.default_environment];
    return (envDoc?.parts ?? [])
      .filter((p) => !p.id.includes(':_'))
      .map((p) => ({ id: p.id, label: p.label, kind: p.kind }));
  }, [runtimeMap.data, runtimeEnv]);
  const sources = useMentionSources(
    projectId.length > 0 ? projectId : undefined,
    mentionsWanted,
    board?.parts ?? EMPTY_SOURCES.parts,
    runtimeEntities,
  );
  const candidates = useCallback(
    (query: string) => {
      if (!mentionsWanted) setMentionsWanted(true);
      return mentionCandidates(query, sources);
    },
    [mentionsWanted, sources],
  );
  // The tokens decide the picked set: the board's parts among them, in the
  // drawing's order. The pane reads it to mark the drawing.
  const onTokensChange = useCallback(
    (next: readonly MentionCandidate[]) => {
      if (board === null) {
        setDiagramSelection(null);
        return;
      }
      const labels = orderedSelection(
        board.parts,
        next.filter((t) => t.kind === 'part').map((t) => t.id),
      );
      setDiagramSelection(
        labels.length > 0 ? { sessionId, diagramId: board.diagram_id, labels } : null,
      );
    },
    [board, sessionId, setDiagramSelection],
  );

  // A fetch that came back empty-handed is said out loud (pattern: routes.tsx
  // RepoNotFound): a deleted or foreign sid used to render a fully functional-
  // looking blank chat that would happily take a question into the void.
  if (session.isError) {
    return (
      <div
        data-testid="session-not-found"
        className="flex h-full min-h-0 flex-col items-start justify-center gap-1 bg-surface px-8"
      >
        <h1 className="mt-0 mb-2 text-[length:var(--text-title-size)] leading-[var(--text-title-line)] font-semibold">
          This conversation isn&rsquo;t here
        </h1>
        <p className="mt-0 text-secondary">
          It may have been deleted, or it belongs to another account. (
          {session.error.message})
        </p>
        <p className="text-secondary">
          <Link
            to={productPath(productId)}
            className="text-(--accent) underline underline-offset-2"
          >
            ← Back to the product
          </Link>
        </p>
      </div>
    );
  }

  // Loading, with nothing local to show: a skeleton, never a blank chat. A
  // just-created session is the exception on purpose — its optimistic echo
  // and live stream ARE the state (EmptyState: "the session page opens
  // mid-thought, never blank"), so the transcript renders them while the row
  // fetch catches up.
  const hasLocalTurn =
    live.pendingUserText !== null || live.frames.length > 0 || live.streaming;
  if (session.isPending && !hasLocalTurn) {
    return (
      <div data-testid="session-pending" className="flex h-full min-h-0 flex-col bg-surface">
        <div className="flex min-h-8 shrink-0 items-center border-b border-default py-1.5 pl-4">
          <SkeletonLine width="40%" height={14} />
        </div>
        <div className="space-y-2 px-5 py-6">
          <SkeletonLine height={12} />
          <SkeletonLine width="85%" height={12} />
          <SkeletonLine width="60%" height={12} />
        </div>
      </div>
    );
  }

  const title = session.data?.title ?? 'New chat';
  const messages = session.data?.messages ?? [];
  // Prices the export estimate. The row's OWN model, not the one that wrote
  // whichever depth is being read: the export is a fresh server-side pass on
  // the session's model, so the last model this session actually ran is the
  // right proxy for what it will cost.
  const lastModel =
    [...messages].reverse().find((m) => m.role === 'assistant' && m.model !== null)?.model ??
    null;

  return (
    <div className="relative flex h-full min-h-0 flex-col bg-surface">
      {/* ONE row (pr-10 clears the column's layout-menu button): ← · title ·
          Export. The provenance line and the model/effort chip both left in
          round 3; what is left fits a single 32px row at any width, and the
          title truncates rather than pushing Export off the edge. */}
      <header className="flex min-h-8 shrink-0 items-center gap-2 border-b border-default py-1.5 pr-10 pl-4">
        <button
          type="button"
          data-testid="session-home"
          title="All sessions"
          onClick={() =>
            onHome !== undefined ? onHome() : void navigate(productPath(productId))
          }
          className="h-6 shrink-0 cursor-pointer border-none bg-transparent p-0 text-tertiary hover:text-primary"
        >
          ←
        </button>
        <div
          data-testid="session-title"
          className="min-w-0 flex-1 truncate text-[length:var(--text-ui-size)] leading-6 font-medium text-primary"
        >
          {title}
        </div>
        {session.data !== undefined ? (
          <div data-testid="session-controls" className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              data-testid="session-export"
              title="Turn this session into a clean write-up you can copy or download"
              onClick={() => setExporting(true)}
              className="h-6 shrink-0 cursor-pointer rounded-pill border border-default bg-transparent px-2 text-[length:var(--text-micro-size)] whitespace-nowrap text-tertiary transition-colors duration-(--motion-hover) hover:border-(--accent) hover:text-primary"
            >
              Export…
            </button>
          </div>
        ) : null}
      </header>
      <MessageList
        sessionId={sessionId}
        messages={messages}
        {...(session.data !== undefined ? { session: session.data } : {})}
        onRetry={(text) => {
          // The question is already in the transcript above the error; this
          // asks it again as a new turn, plain (no focus, no tags).
          send(text);
        }}
      />
      <div className="shrink-0 px-4 pb-4">
        {unwrittenRepo !== null ? (
          <div
            data-testid="session-unwritten"
            className="mb-2 flex items-center justify-between gap-3 rounded-card border border-default bg-surface px-3 py-2"
          >
            <span className="text-tertiary">
              {unwrittenRepo.name} is read; the map is not written yet. Answers come from
              reading the code.
            </span>
            <Button kind="default" testId="session-write-map" onClick={() => setWriteMapOpen(true)}>
              Write the map
            </Button>
          </div>
        ) : null}
        {unwrittenRepo !== null && writeMapOpen ? (
          <CostPreviewDialog
            repoId={unwrittenRepo.id}
            open
            onClose={() => setWriteMapOpen(false)}
          />
        ) : null}
        {settingsError !== null ? (
          <p data-testid="session-settings-error" className="mt-0 mb-1.5 text-(--danger)">
            {settingsError}
          </p>
        ) : null}
        <ChatComposer
          placeholder="Ask about your system…"
          onSend={(text, sent) => {
            const partLabels = sent.filter((t) => t.kind === 'part').map((t) => t.id);
            const focus = board !== null ? focusFor(board, partLabels) : null;
            const mentions = sent
              .map((t) => mentionToWire(t, projectId))
              .filter((m): m is NonNullable<typeof m> => m !== null);
            const accepted = send(text, focus, mentions);
            // Refused (a pump is still registered for this session): the
            // composer keeps the draft on false, and this line says why.
            // Taken: the composer empties, the tokens go with it, the
            // pane's marks follow, and the refusal line (only that) clears.
            setSettingsError((prev) =>
              accepted ? (prev === SEND_REFUSED ? null : prev) : SEND_REFUSED,
            );
            return accepted;
          }}
          streaming={streaming}
          onStop={stop}
          width="full"
          draft={composerDraft}
          onDraftConsumed={() => setComposerDraft(null)}
          request={composerRequest}
          onRequestConsumed={() => requestComposer(null)}
          mentionCandidates={candidates}
          onTokensChange={onTokensChange}
          controls={
            session.data !== undefined ? (
              <>
                <SessionPickers
                  sessionId={sessionId}
                  productId={productId}
                  sessionModel={session.data.model}
                  sessionEffort={session.data.effort}
                  streaming={streaming}
                  onError={setSettingsError}
                />
                {/* Round 7: the context percentage and the session spend, at
                    the right-hand end of the same row. What will run, then
                    what it is costing, then Send. */}
                <SessionMeter messages={messages} />
              </>
            ) : null
          }
        />
      </div>
      {exporting ? (
        <Suspense fallback={null}>
          <ExportDialog
            sessionId={sessionId}
            model={lastModel}
            onClose={() => setExporting(false)}
          />
        </Suspense>
      ) : null}
    </div>
  );
}

export default function ChatPanel({ sessionId }: { sessionId?: Ulid } = {}) {
  // repoId scopes status/artifacts; productId scopes sessions (api.md §10).
  const { repoId: projectId, productId } = useWorkspaceIds();

  // NO unmount abort: the module-scoped streams survive collapse AND any
  // route-away — the sidebar session statuses own the way back, and only
  // the shell-level unmount (logout) or the Stop button aborts.

  if (sessionId === undefined) {
    // Only the route-scoped session view remains (Stage B); with no sid the
    // workspace has nothing to show — the product home owns "no session".
    return null;
  }
  return <SessionView projectId={projectId} productId={productId} sessionId={sessionId} />;
}
